import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoModules from 'mojito/modules';
import { useCallback, useState, useMemo, useRef, useEffect, Fragment } from 'react';
import { useSelector } from 'react-redux';
import MojitoServices from 'mojito/services';
import Search from 'applications/sports/components/search/index.js';
import SearchOverlay from 'applications/sports/components/search/search-overlay/index.js';

const {
    ContextExtender: AnalyticsContextExtender,
    Context: AnalyticsContext,
    ContextBuilder: AnalyticsContextBuilder,
} = MojitoCore.Presentation.AnalyticsContext;
const { useComponentVisibilityAfterScrollRestoration } = MojitoPresentation.Hooks;
const { NextRaces, QuickStartGuideBanner, QuickStartGuide, CmsCouponsList } = MojitoModules;
const CmsCouponMenuLinks = MojitoModules.CmsCouponMenuLinks.module;
const { TOP_SPORTS_PRIMARY_TAB_ID, TOP_SPORTS_SECONDARY_TAB_ID } =
    MojitoModules.CmsCouponMenuLinks.constants;
const { useMenuContent } = MojitoModules.Common.Hooks;
const { selectTimeZoneGmt } = MojitoServices.UserSettings.selectors;

const { BannerPromotions, MenuLinkPromotions, PromotionUtils, PrebuiltPromotionCards } =
    MojitoModules.Promotions;
const { usePromotionsLoadDone } = MojitoModules.Promotions.hooks;
const {
    FlexPane,
    DocumentHead,
    GlobalLinks,
    PageSplitLayout,
    DefaultHeadMetaFactory,
    Button,
    PostponedRenderer,
} = MojitoPresentation.Components;
const EventCardsCarousel = MojitoModules.EventCardsCarousel.module;
const PageContextMenu = MojitoModules.PageContextMenu;
const SportsMenu = MojitoModules.SportsMenu.module;
const TopSports = MojitoModules.TopSports.module;
const AnalyticsTypes = MojitoServices.Analytics.types;
const { HOME_PAGE } = AnalyticsTypes.SOURCE;

const extendAnalyticsContext = (context, value) => ({ ...context, ...value });
const selectionSourceContext = new AnalyticsContextBuilder().withAnalyticsPath(HOME_PAGE).build();
const analyticsContext = {
    marketChangeAnalyticsType: 'homePageEventListMarketSelectorValueChanged',
};

const applicablePage = 'homepage';

const HomePage = props => {
    const {
        mojitoTools: { config, l10n },
    } = props;
    const metaFactory = config.documentHeadMetaFactory || DefaultHeadMetaFactory;
    const documentHeadMetaFactory = useMemo(() => new metaFactory(l10n), [metaFactory, l10n]);
    const headData = {
        title: '$HOME_PAGE.PAGE_TITLE',
        keywords: '$HOME_PAGE.PAGE_KEYWORDS',
        description: '$HOME_PAGE.PAGE_DESCRIPTION',
        canonical: '$HOME_PAGE.PAGE_CANONICAL',
    };

    const timeZone = useSelector(state => selectTimeZoneGmt(state));
    useMenuContent('', timeZone);

    return (
        <>
            <DocumentHead data={headData} metaFactory={documentHeadMetaFactory} />
            <PageSplitLayout>
                <FlexPane config={config.primaryContentLayout} class="ta-primaryContentLayout">
                    <LayoutBuilder {...props} layoutParts={layoutParts} />
                </FlexPane>

                <FlexPane config={config.secondaryContentContainer}>
                    <PageContextMenu
                        config={config.leftContextMenu}
                        applicablePage={applicablePage}
                    />
                    <SportsMenu config={config.sportsMenu} />
                    <GlobalLinks />

                    {PromotionUtils.hasLocationId(config.leftPromotions) && (
                        <BannerPromotions
                            config={config.leftPromotions}
                            applicablePage={applicablePage}
                        />
                    )}
                </FlexPane>
            </PageSplitLayout>
        </>
    );
};

const layoutParts = {
    TopPromotions: props => <TopPromotionsPart {...props} />,
    Search: props => <SearchPart {...props} />,
    EventCardsCarousel: props => <EventCardsCarouselPart {...props} />,
    QuickStartGuide: props => (props.showQuickStartGuide ? <QuickStartGuide /> : null),
    PageContextMenu: props => <PageContextMenuPart {...props} />,
    QuickStartGuideBanner: props => <QuickStartGuideBannerPart {...props} />,
    PrebuiltPromotionCards: props => <PrebuiltPromotionCardsPart {...props} />,
    NextRaces: props => <NextRacesPart {...props} />,
    TopSportsPrimary: props => <TopSportsPrimaryPart {...props} />,
    TopSportsSecondary: props => <TopSportsSecondaryPart {...props} />,
    CmsCouponsList: props => <CmsCouponsListPart {...props} />,
    CmsCouponMenuLinks: props => <CmsCouponMenuLinksPart {...props} />,
};

const LayoutBuilder = props => {
    const {
        layoutParts,
        mojitoTools: { config },
    } = props;
    // Retrieve promotionsLoaded here to ensure a consistent sequential re-rendering cycle of each
    // postponed element once promotions arrive. This is needed to avoid a layout shift when promotions render.
    const promotionsLoaded = usePromotionsLoadDone();
    const prevPromotionsLoadedRef = useRef(promotionsLoaded);
    const persistOnRerender = !prevPromotionsLoadedRef.current && promotionsLoaded;

    useEffect(() => {
        prevPromotionsLoadedRef.current = promotionsLoaded;
    }, [promotionsLoaded]);

    let postponedPriority = 0;

    return Object.entries(config.layoutParts)
        .sort(([, paramsA], [, paramsB]) => paramsA.position - paramsB.position)
        .filter(([name, params]) => layoutParts[name] && params.show)
        .map(([name, params]) => {
            const Component = layoutParts[name];
            let content = <Component {...props} />;

            if (params.postponed) {
                const priority = postponedPriority;
                postponedPriority += 1;
                content = (
                    <PostponedRenderer
                        name={name}
                        priority={priority}
                        persistOnRerender={persistOnRerender}
                    >
                        {content}
                    </PostponedRenderer>
                );
            }

            if (params.awaitPromotions) {
                content = (
                    <AwaitPromotions config={config} promotionsLoaded={promotionsLoaded}>
                        {content}
                    </AwaitPromotions>
                );
            }

            return <Fragment key={name}>{content}</Fragment>;
        });
};

const TopPromotionsPart = ({ mojitoTools: { appContext, config } }) => {
    const onTopPromotionsLinkClick = menuItem => {
        appContext.analyticsEmitter.emitAnalytics('sportsCarouselLinkClicked', menuItem.id);
    };

    return (
        <FlexPane config={config.topPromotionsContainer}>
            {PromotionUtils.hasLocationId(config.topLinkPromotions) && (
                <MenuLinkPromotions
                    config={config.topLinkPromotions}
                    applicablePage={applicablePage}
                    onLinkClick={onTopPromotionsLinkClick}
                />
            )}
            {PromotionUtils.hasLocationId(config.topBannerPromotions) && (
                <BannerPromotions
                    config={config.topBannerPromotions}
                    applicablePage={applicablePage}
                />
            )}
        </FlexPane>
    );
};

const EventCardsCarouselPart = ({ mojitoTools: { config } }) => (
    <EventCardsCarousel config={config.eventCardsCarousel} pageId={applicablePage} />
);

const PageContextMenuPart = ({ mojitoTools: { config }, contextMenuKey = 'topContextMenu' }) => (
    <PageContextMenu config={config[contextMenuKey]} applicablePage={applicablePage} />
);

const QuickStartGuideBannerPart = ({ mojitoTools: { config } }) => (
    <FlexPane config={config.quickStartGuideBannerContainer}>
        <QuickStartGuideBanner />
    </FlexPane>
);

const SearchPart = props => {
    const {
        mojitoTools: { config },
    } = props;
    const [isSearchOpen, setIsSearchOpen] = useState(false);

    return isSearchOpen ? (
        <SearchOverlay isOpen={isSearchOpen} onSearchClose={() => setIsSearchOpen(false)} />
    ) : (
        <Button config={config.searchButton} onClick={() => setIsSearchOpen(true)}>
            <Search config={config.searchControllerView} />
        </Button>
    );
};

const NextRacesPart = props => {
    const { mojitoTools } = props;
    const { config, stringResolver } = mojitoTools;
    const nextRaces = config.nextRacesSports.map(nextRaceConfig => {
        return (
            nextRaceConfig.show && (
                <NextRaces
                    applicablePage={applicablePage}
                    key={nextRaceConfig.sportId}
                    sportId={nextRaceConfig.sportId}
                    config={config.nextRacesController}
                    title={stringResolver.resolveString(
                        `$HOME_PAGE.NEXT_OFF.${nextRaceConfig.sportId.toUpperCase()}`
                    )}
                />
            )
        );
    });

    if (!nextRaces.some(nextRace => !!nextRace)) {
        return null;
    }

    return <>{nextRaces}</>;
};

const PrebuiltPromotionCardsPart = ({ mojitoTools: { config } }) => (
    <AnalyticsContext.Provider value={selectionSourceContext}>
        <PrebuiltPromotionCards
            applicablePage={applicablePage}
            config={config.prebuiltPromotionCards}
        />
    </AnalyticsContext.Provider>
);

const TopSportsPrimaryPart = props => {
    const { mojitoTools } = props;
    const { config, stringResolver } = mojitoTools;
    const { primary: primaryCoupon } = config.topSportCoupons;

    return (
        <AnalyticsContextExtender
            value={analyticsContext}
            extendingStrategy={extendAnalyticsContext}
        >
            <TopSportsContent mojitoTools={mojitoTools} sectionId={TOP_SPORTS_PRIMARY_TAB_ID}>
                <TopSports
                    config={primaryCoupon.coupon}
                    navigationTitle={stringResolver.resolveString(
                        '$HOME_PAGE.PRIMARY_TOP_SPORTS_TITLE'
                    )}
                />
            </TopSportsContent>
        </AnalyticsContextExtender>
    );
};

const TopSportsSecondaryPart = props => {
    const { mojitoTools } = props;
    const { config, stringResolver } = mojitoTools;
    const { secondary: secondaryCoupon } = config.topSportCoupons;

    return (
        <AnalyticsContextExtender
            value={analyticsContext}
            extendingStrategy={extendAnalyticsContext}
        >
            <TopSportsContent mojitoTools={mojitoTools} sectionId={TOP_SPORTS_SECONDARY_TAB_ID}>
                <TopSports
                    config={secondaryCoupon.coupon}
                    navigationTitle={stringResolver.resolveString(
                        '$HOME_PAGE.SECONDARY_TOP_SPORTS_TITLE'
                    )}
                />
            </TopSportsContent>
        </AnalyticsContextExtender>
    );
};

const TopSportsContent = props => {
    const { children, sectionId, mojitoTools } = props;
    const { config, style } = mojitoTools;
    const [elementRef, visible] = useTopSportsVisibility(config);
    const hasCmsCouponsList = config.layoutParts.CmsCouponsList?.show;
    const topSportsConfig = hasCmsCouponsList
        ? style.topSportsContainer
        : config.topSportsContainer;
    return (
        <FlexPane
            id={sectionId}
            elementRef={elementRef}
            class="ta-TopSportsSection"
            config={topSportsConfig}
        >
            {visible ? children : null}
        </FlexPane>
    );
};

const CmsCouponMenuLinksPart = props => {
    const {
        mojitoTools: { config, stringResolver, appContext },
    } = props;
    const { subscriptionResolver } = appContext;
    const listId = subscriptionResolver.cmsCouponPrefixHomepage();
    const { primary: primaryCoupon = {}, secondary: secondaryCoupon = {} } =
        config.topSportCoupons || {};
    const showPrimaryTopSportsTab =
        !!config.layoutParts.TopSportsPrimary?.show && !primaryCoupon.hide;
    const showSecondaryTopSportsTab =
        !!config.layoutParts.TopSportsSecondary?.show && !secondaryCoupon.hide;

    if (!config.layoutParts.CmsCouponsList?.show) {
        return null;
    }

    return (
        <CmsCouponMenuLinks
            config={config.cmsCouponMenuLinks}
            couponsListId={listId}
            showPrimaryTopSportsTab={showPrimaryTopSportsTab}
            showSecondaryTopSportsTab={showSecondaryTopSportsTab}
            primaryTopSportsTabLabel={stringResolver.resolveString(
                '$HOME_PAGE.PRIMARY_STICKY_TAB_TOP_SPORTS_TITLE'
            )}
            secondaryTopSportsTabLabel={stringResolver.resolveString(
                '$HOME_PAGE.SECONDARY_TOP_SPORTS_TITLE'
            )}
        />
    );
};

const CmsCouponsListPart = ({ mojitoTools: { config, appContext } }) => {
    const { subscriptionResolver } = appContext;
    const listId = subscriptionResolver.cmsCouponPrefixHomepage();
    return <CmsCouponsList config={config.cmsCouponsList} couponsListId={listId} />;
};

const isElementInViewport = el => {
    if (!el) {
        return false;
    }
    const rect = el.getBoundingClientRect();
    const isInRange = (num, start, end) => {
        const leftInRange = num >= start;
        const rightInRange = num < end;
        return leftInRange && rightInRange;
    };
    return (
        (isInRange(rect.top, 0, window.innerHeight) ||
            isInRange(rect.bottom, 0, window.innerHeight)) &&
        (isInRange(rect.left, 0, window.innerWidth) || isInRange(rect.right, 0, window.innerWidth))
    );
};

const useTopSportsVisibility = config => {
    const [visible, setVisible] = useState(!config.topSportCoupons.optimizeRendering);
    const onShowUp = useCallback(() => setVisible(true), []);
    const visibilityRef = useComponentVisibilityAfterScrollRestoration(onShowUp);
    const elementRef = useCallback(
        element => {
            // We will detect if element is in view port even before onShowUp handler will be called.
            // This is needed to properly report PAGE_READY metric once prefetch content is used in conjunction with optimizeRendering.
            if (isElementInViewport(element)) {
                setVisible(true);
            }
            visibilityRef(element);
        },
        [visibilityRef]
    );
    return [elementRef, visible];
};

// Wait until promotions have finished loading when the layout part has awaitPromotions enabled
// and the page has promotion-aware components configured.
const AwaitPromotions = ({ config, promotionsLoaded, children }) => {
    const { layoutParts } = config;
    const hasTopPromotions =
        layoutParts.TopPromotions?.show &&
        (PromotionUtils.hasLocationId(config.topBannerPromotions) ||
            PromotionUtils.hasLocationId(config.topLinkPromotions));
    const hasNoConfiguredPromotionContent =
        !hasTopPromotions &&
        !layoutParts.EventCardsCarousel?.show &&
        !layoutParts.PrebuiltPromotionCards?.show;
    const promotionsComplete = promotionsLoaded || hasNoConfiguredPromotionContent;

    if (!promotionsComplete) {
        return null;
    }

    return children;
};

HomePage.ALL_SPORTS_CODE = 'ALL';

HomePage.getStyle = (config, mode, merge) => {
    const scrollOffset = config.topSportCoupons.scrollOffset;
    return {
        margin: config.margin,
        display: 'flex',
        flexDirection: 'column',
        topSportsContainer: merge(config.topSportsContainer, {
            style: { scrollMargin: `${scrollOffset} 0 0 0` },
        }),
    };
};

export default HomePage;
